import React, { Fragment, useEffect } from "react";
import { Dialog, Portal, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { cn } from "./cn";
import Button from "./Button";

const themeOverrides = [];
let originalTheme = null;

/**
 * Bun modal: scrim, radius-14 surface, modal shadow. Compose the body as
 * children and pass footer actions (right-aligned) via `footer`.
 * Theme note: this portals to <body>, which is why the dark theme attribute
 * lives on <html> (see ui/theme.js) rather than a wrapper div.
 */
export default function Modal({
  open,
  onClose,
  title,
  subtitle,
  initialFocus,
  leading,
  headerActions,
  footer,
  width = 420,
  showClose = false,
  closeLabel = "Close dialog",
  bodyClassName,
  bodyProps,
  layerClassName,
  fullscreen = false,
  scrollBody = false,
  ariaLabel,
  portal,
  theme,
  variant,
  children,
}) {
  useEffect(() => {
    if (!open || !theme) return undefined;
    const root = document.documentElement;
    if (themeOverrides.length === 0) originalTheme = root.getAttribute('data-bun-theme');
    const override = { theme };
    themeOverrides.push(override);
    root.setAttribute('data-bun-theme', theme);
    return () => {
      themeOverrides.splice(themeOverrides.indexOf(override), 1);
      const restored = themeOverrides.length ? themeOverrides[themeOverrides.length - 1].theme : originalTheme;
      if (restored === null) root.removeAttribute('data-bun-theme');
      else root.setAttribute('data-bun-theme', restored);
    };
  }, [open, theme]);
  return (
    <Transition show={!!open} as={Fragment}>
      <Dialog onClose={onClose} initialFocus={initialFocus} aria-label={ariaLabel} className={cn("relative z-50 font-instrument", layerClassName)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-[color:var(--bun-scrim)]" aria-hidden="true" />
        </Transition.Child>
        <div className={cn("fixed inset-0 flex items-center justify-center", fullscreen ? "p-0" : "p-4 sm:p-6", variant === 'notice' && 'items-end p-0 sm:items-center sm:p-6')}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 translate-y-2"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Panel
              data-bun-dialog-panel
              style={fullscreen ? undefined : { width }}
              className={cn(
                "max-w-full bg-bun-surface text-bun-text shadow-bun-modal",
                fullscreen ? "flex h-full w-full flex-col" : "rounded-bun-xl max-h-[calc(100dvh-2rem)] overflow-y-auto",
                scrollBody && "flex flex-col overflow-hidden sm:max-h-[calc(100dvh-3rem)]",
                variant === 'notice' && "relative w-full rounded-b-none sm:rounded-b-bun-xl"
              )}
            >
              {variant === 'notice' && <div aria-hidden="true" className="mx-auto mt-3 h-1 w-9 shrink-0 rounded-full bg-bun-track-off sm:hidden" />}
              {(title || subtitle || showClose || leading || headerActions) && (
                <div className={cn("flex shrink-0 items-start gap-4 px-6 pt-5 pb-1", leading && "items-center gap-2 sm:gap-4", variant === 'notice' && "flex-col items-start gap-3 pt-6 sm:flex-row sm:items-start", variant === 'notice' && leading && 'sm:pl-20')}>
                  {leading && <div className={cn("shrink-0", variant === 'notice' && 'sm:absolute sm:left-6 sm:top-6')}>{leading}</div>}
                  <div className="min-w-0 flex-1">
                    {title && (
                      <Dialog.Title className="m-0 font-instrument text-bun-heading font-semibold leading-snug tracking-normal">
                        {title}
                      </Dialog.Title>
                    )}
                    {subtitle && (
                      <Dialog.Description className="m-0 mt-[2px] text-bun-meta font-book leading-relaxed text-bun-stone">
                        {subtitle}
                      </Dialog.Description>
                    )}
                  </div>
                  {headerActions && <div className="flex shrink-0 items-center gap-2">{headerActions}</div>}
                  {showClose && (
                    <Button
                      variant="secondary"
                      size="sm"
                      iconOnly
                      onClick={() => onClose(false)}
                      aria-label={closeLabel}
                      className="-mr-2 -mt-1 flex-none"
                    >
                      <XIcon className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  )}
                </div>
              )}
              <div
                {...bodyProps}
                className={cn(
                  "px-6 py-[14px] text-bun-body font-book text-bun-slate",
                  (fullscreen || scrollBody) && "min-h-0 flex-1 overflow-y-auto",
                  variant === 'workspace' && 'p-0 overflow-hidden',
                  variant === 'notice' && 'pt-[6px]',
                  variant === 'notice' && leading && 'sm:pl-20',
                  bodyClassName,
                  bodyProps?.className
                )}
              >
                {children}
              </div>
              {footer && (
                <div
                  className={cn(
                    "flex shrink-0 justify-end gap-2 px-6 pb-[18px] pt-1",
                    variant === 'notice' && 'justify-start pb-8 [&>*]:w-full sm:[&>*]:w-auto',
                    variant === 'notice' && leading && 'sm:pl-20'
                  )}
                >
                  {footer}
                </div>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
        {portal && <Portal>{portal}</Portal>}
      </Dialog>
    </Transition>
  );
}
